import type { FormEvent } from 'react'
import { lazy, Suspense, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ChevronRight, QrCode } from 'lucide-react'
import { api } from '@/lib/api'
import { cn } from '@/lib/cn'
import { formatDate } from '@/lib/date'
import { useAuth } from '@/context/AuthContext'
import type { Job, Paginated } from '@/types'
import Card from '@/components/ui/Card'
import PageHeader from '@/components/ui/PageHeader'
import Button from '@/components/ui/Button'
import { Input } from '@/components/ui/Field'
import BalanceBadge from '@/components/job/BalanceBadge'
import UrgentBadge from '@/components/job/UrgentBadge'
import { StateBadge } from '@/components/ui/Badge'
import { Table, Row, Cell, EmptyRow, actionsColumn, actionsCellClass } from '@/components/ui/Table'
import QrScanner from '@/components/staff/QrScanner'

// Lazy so the dotLottie player only downloads on this screen. Until it has
// drawn its first frame, the box shows scan-qr-poster.png, a still of that
// same frame, so the hand-off is invisible and a player that never loads
// leaves the artwork up instead of an empty box.
const ScanAnimation = lazy(() => import('@/components/staff/ScanAnimation'))

export default function StaffScan() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [code, setCode] = useState('')
  const [scanOpen, setScanOpen] = useState(false)
  const [animReady, setAnimReady] = useState(false)

  // First page of the live queue as a quick pick - the full list, with
  // history and search, lives at /staff/jobs.
  const queue = useQuery({
    queryKey: ['staff-jobs', 'queue'],
    queryFn: async () =>
      (await api.get<Paginated<Job>>('/staff/jobs', { params: { scope: 'queue' } })).data.data,
  })

  const open = (e: FormEvent) => {
    e.preventDefault()
    if (code.trim()) navigate(`/staff/jobs/${encodeURIComponent(code.trim())}`)
  }

  return (
    <div>
      <PageHeader
        title="Scan job QR"
        subtitle={`${user?.department?.name ?? 'Production'} section queue`}
      />

      <Card className="mx-auto max-w-xl border-2 border-dashed border-brand-300 text-center">
        <button
          type="button"
          onClick={() => setScanOpen(true)}
          className="mx-auto block w-full rounded-xl py-1 transition-colors hover:bg-foreground/5"
        >
          <div className="relative mx-auto h-24 w-24">
            <img src="/scan-qr-poster.png" alt="" hidden={animReady} className="size-full" />
            <Suspense fallback={null}>
              <ScanAnimation onReady={() => setAnimReady(true)} />
            </Suspense>
          </div>
          <p className="mt-2 font-bold">Tap to scan the job sheet QR</p>
          <p className="mt-1 text-xs text-muted">
            Opens your camera - or type the job code below.
          </p>
        </button>
        <form onSubmit={open} className="mx-auto mt-4 flex max-w-sm gap-2">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. MPG-2026-00005"
            className="text-center font-mono"
          />
          <Button type="submit">Open</Button>
        </form>
      </Card>

      <QrScanner open={scanOpen} onClose={() => setScanOpen(false)} />

      <h2 className="mb-3 mt-8 text-sm font-bold uppercase tracking-wide text-muted">
        Jobs waiting at {user?.department?.name}
      </h2>
      {/* Phones get tappable cards: the five-column table pushes its Open button off a 375px screen. */}
      <div className="space-y-3 sm:hidden">
        {(queue.data?.length ?? 0) === 0 && (
          <div className="box px-4 py-10 text-center text-sm text-foreground/60">
            No jobs in your section right now.
          </div>
        )}
        {queue.data?.map((job) => (
          <button
            key={job.id}
            type="button"
            onClick={() => navigate(`/staff/jobs/${job.qr_token}`)}
            className="box block w-full p-4 text-left transition-colors active:bg-foreground/5"
          >
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
              <span className="whitespace-nowrap font-mono text-xs font-bold">{job.code}</span>
              <span className="flex items-center gap-1.5">
                <BalanceBadge balance={job.balance} />
                <UrgentBadge urgent={job.order?.is_urgent} />
                <StateBadge state={job.status} />
              </span>
            </div>
            <div className="mt-2.5 flex items-center justify-between gap-3 text-sm">
              <span className="min-w-0">
                <span className="block truncate text-foreground">{job.order?.agent?.name}</span>
                <span className="mt-0.5 block text-xs text-foreground/60">{job.order?.number}</span>
                {job.current_department_due && (
                  <span className={cn('mt-0.5 block text-xs', job.late ? 'font-semibold text-status-danger' : 'text-foreground/60')}>
                    Due {formatDate(job.current_department_due)}
                  </span>
                )}
              </span>
              <ChevronRight className="size-4 shrink-0 text-foreground/40" />
            </div>
          </button>
        ))}
      </div>

      <div className="hidden sm:block">
        <Table columns={['Job code', 'Order', 'Customer', 'Due', 'Status', actionsColumn]}>
          {(queue.data?.length ?? 0) === 0 && <EmptyRow colSpan={6}>No jobs in your section right now.</EmptyRow>}
          {queue.data?.map((job) => (
            <Row key={job.id}>
              <Cell className="font-mono text-xs font-bold">
                {job.code}
                <UrgentBadge urgent={job.order?.is_urgent} className="ml-1.5" />
                <BalanceBadge balance={job.balance} className="ml-1.5" />
              </Cell>
              <Cell>{job.order?.number}</Cell>
              <Cell>{job.order?.agent?.name}</Cell>
              {/* Jobs without a section deadline keep the cell quiet - a red
                  bold "-" reads as an alarm about nothing. */}
              <Cell className={cn(job.late && 'font-semibold text-status-danger')}>
                {job.current_department_due ? formatDate(job.current_department_due) : '-'}
              </Cell>
              <Cell>
                <StateBadge state={job.status} />
              </Cell>
              <Cell className={actionsCellClass}>
                <Button variant="outline" onClick={() => navigate(`/staff/jobs/${job.qr_token}`)}>
                  <QrCode className="size-4" /> Open
                </Button>
              </Cell>
            </Row>
          ))}
        </Table>
      </div>
    </div>
  )
}
